/* eslint-disable no-magic-numbers */
/* eslint-disable complexity */
import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import get from 'lodash/get';
import { format } from 'date-fns';

import { UILoader, toastError, toastSuccess } from '@futrli/ui-components';
import { FormattedMessage, useIntl } from '@futrli/localisation';
import { WorkspaceMember } from '@futrli-sdk/people';
import { Logger } from '@futrli/shared-services';
import { EmailExportedReport } from '@futrli-sdk/report_scheduling';
import { CreateReportSchedule } from '@futrli-sdk/report_scheduling';

import { AuthStoreState } from '../../../core/types';
import { createScheduleReport } from '../../../core/api/reporting/report-scheduling';
import { emailExportedReport } from '../../../core/api/reporting/report-document-export';
import { invalidateDispatchedReports, invalidateReportSchedules, invalidateAwaitingReviewReports } from '@futrli/views/mutations/invalidateListingQueries';
import { ReportingSidebar } from '@futrli/predict/report-builder/components/reporting-sidebar/reporting-sidebar';
import { trackEvent } from '../../../core/tracker';
import { TRACKING_EVENTS } from '../../../core/constants';
import { useMembersContext } from '../../../report-builder/members-context';
import { useOrganistionName } from '../../../hooks/useOrganisationName';
import { usePredictionsInfoContext } from '../../../forecast/detail/context/predictions-info-context';

import Textarea from 'carbon-react/lib/components/textarea';
import Form from 'carbon-react/lib/components/form';
import Button from 'carbon-react/lib/components/button';
import { MultiSelect, Option } from 'carbon-react/lib/components/select';
import Textbox from 'carbon-react/lib/components/textbox';

import * as styles from './dialog.scss';

interface Props {
  isOpen: boolean;
  handleClose: () => void;
  exportedReportId?: string;
  reportName?: string;
  reportId?: string;
  revalidateData?: () => Promise<void>;
}

const MAX_NUMBER_OF_USERS = 30;

export const SendEmailDialog = ({
  isOpen,
  handleClose,
  reportName,
  exportedReportId,
  reportId,
  revalidateData
}: Props) => {
  const intl = useIntl();
  const [selectedUsers, setSelectedUsers] = useState<Record<string, unknown>[]>([]);
  const [emailSubject, setEmailSubject] = useState('');
  const [emailBody, setEmailBody] = useState('');
  const [isSendingEmail, setIsSendingEmail] = useState(false);

  const { workspaceId: tempWorkspaceId, organisationId: tempOrganisationId } = useParams();
  const workspaceId = tempWorkspaceId || '';
  const organisationId = tempOrganisationId || '';
  const { isConsolidation } = usePredictionsInfoContext();

  const { isLoading, members } = useMembersContext();

  const memberOptions = useMemo(
    () =>
      (members || []).map(m => ({
        ...m,
        text: `${m.name.first_name} ${m.name.last_name} <${m.email_address}>`
      })),
    [members]
  );

  const organisationName = useOrganistionName();

  const fullName = useSelector((store: AuthStoreState) => {
    const user = store.authReducers.authenticatedUser;
    const givenName = get(user, 'attributes.given_name', '');
    const familyName = get(user, 'attributes.family_name', '');
    return !familyName ? givenName : `${givenName} ${familyName}`;
  });

  useEffect(() => {
    setEmailBody(
      intl.formatMessage(
        {
          id: 'predict.default_report_email_body_v3',
          defaultMessage: `Hello,\n\nI’ve prepared the report {reportName} for you. Please find it attached as a PDF.\n\nAll the best,\n\n{fullName}`
        },
        { reportName, fullName }
      )
    );
  }, [fullName, reportName, intl]);

  useEffect(() => {
    setEmailSubject(
      intl.formatMessage(
        {
          id: 'predict.default_report_email_subject',
          defaultMessage: `You have a new report for {organisationName}`
        },
        { organisationName }
      )
    );
  }, [organisationName, intl]);

  const handleEmailSubjectChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setEmailSubject(event.target.value);
  };

  const handleEmailBodyChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    setEmailBody(event.target.value);
  };

  const handleSendClick = async () => {
    try {
      setIsSendingEmail(true);
      const payload: EmailExportedReport = {
        body: emailBody,
        subject: emailSubject,
        users: ((selectedUsers as unknown) as WorkspaceMember[]).map(s => ({
          id: s.id,
          email_address: s.email_address
        }))
      };
      if (exportedReportId) {
        await emailExportedReport(workspaceId, organisationId, exportedReportId, payload, isConsolidation);
        invalidateDispatchedReports(organisationId, workspaceId, isConsolidation);
      } else if (reportId) {
        const sharedPayloadData: Partial<CreateReportSchedule> = {
          name: reportName,
          report: reportId,
          period: 'do-not-repeat',
          start_date: format(new Date(), 'yyyy-MM-dd'),
          email_subject: emailSubject,
          email_body: emailBody,
          users: ((selectedUsers as unknown) as WorkspaceMember[]).map(s => ({
            id: s.id,
            email_address: s.email_address
          })),
          approval_email_user: false,
          end_type: 'does-not-end'
        };
        await createScheduleReport(workspaceId, organisationId, sharedPayloadData, isConsolidation);
        invalidateReportSchedules(organisationId, workspaceId, isConsolidation);
        invalidateAwaitingReviewReports(organisationId, workspaceId, isConsolidation);
        revalidateData?.();
      }
      trackEvent(
        isConsolidation ? TRACKING_EVENTS.CREATE_CONSOLIDATION_PDF_AND_SEND : TRACKING_EVENTS.SENT_INSTANT_PDF
      );
      handleClose();
      toastSuccess(<FormattedMessage id="predict.email_has_been_sent" defaultMessage="Email has been sent" />);
    } catch (e) {
      Logger.error(e as string);
      toastError();
    } finally {
      setIsSendingEmail(false);
    }
  };

  const isSubmitDisabled =
    !emailBody || !emailSubject || selectedUsers.length === 0 || selectedUsers.length > MAX_NUMBER_OF_USERS;

  if (isLoading || isSendingEmail) {
    return (
      <ReportingSidebar
        aria-label={intl.formatMessage({ id: 'predict.send_pdf_report_heading', defaultMessage: 'Send PDF report' })}
        title={intl.formatMessage({ id: 'predict.send_pdf_report_heading', defaultMessage: 'Send PDF report' })}
        onCancel={handleClose}
        open={isOpen}
        sidebarProps={{
          size: 'large'
        }}
      >
        <UILoader />
      </ReportingSidebar>
    );
  }

  return (
    <ReportingSidebar
      aria-label={intl.formatMessage({ id: 'predict.send_pdf_report_heading', defaultMessage: 'Send PDF report' })}
      title={intl.formatMessage({ id: 'predict.send_pdf_report_heading', defaultMessage: 'Send PDF report' })}
      onCancel={handleClose}
      open={isOpen}
      sidebarProps={{
        size: 'large'
      }}
    >
      <Form
        stickyFooter
        saveButton={
          <Button buttonType="primary" size="small" onClick={handleSendClick} disabled={isSubmitDisabled}>
            {<FormattedMessage id="predict.send_pdf_report_button" defaultMessage="Send PDF report" />}
          </Button>
        }
        onSubmit={handleSendClick}
      >
        <div className={styles.dialogContent}>
          <MultiSelect
            name="account"
            id="account"
            label={intl.formatMessage({ id: 'common.to_users', defaultMessage: 'To' })}
            inputHint={intl.formatMessage({
              id: 'predict.only_users_with_access_can_receive_scheduled_reports',
              defaultMessage: 'Only users with access to this organisation can be selected to receive scheduled reports'
            })}
            placeholder={
              selectedUsers.length
                ? ''
                : intl.formatMessage({
                    id: 'predict.start_typing_find_users',
                    defaultMessage: 'Start typing to find users'
                  })
            }
            value={selectedUsers}
            onChange={e => {
              if (typeof e.target.value === 'object') {
                setSelectedUsers(e.target.value);
              }
            }}
            required
          >
            {memberOptions.map(member => (
              <Option key={member.id} text={member.text} value={member} />
            ))}
          </MultiSelect>
          <Textbox
            label={intl.formatMessage({ id: 'common.subject', defaultMessage: 'Subject' })}
            value={emailSubject}
            onChange={handleEmailSubjectChange}
            aria-label={intl.formatMessage({ id: 'common.subject', defaultMessage: 'Subject' })}
            required
          />
          <Textarea
            label={intl.formatMessage({ id: 'common.body', defaultMessage: 'Body' })}
            expandable
            value={emailBody}
            onChange={handleEmailBodyChange}
            aria-label={intl.formatMessage({ id: 'common.body', defaultMessage: 'Body' })}
            rows={8}
            required
          />
        </div>
      </Form>
    </ReportingSidebar>
  );
};
